import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBuilding, faPlus, faSearch, faUsers,
    faEdit, faTrash, faBan, faCheckCircle, faExternalLinkAlt,
    faEnvelope, faPhone, faMapMarkerAlt, faChartPie, faShieldAlt,
    faFileInvoiceDollar, faLayerGroup, faSyncAlt, faTimes,
    faTable, faThLarge, faEye, faEyeSlash, faUserShield,
    faSortAmountDown, faSortAmountUp
} from '@fortawesome/free-solid-svg-icons';
import api from '../api/api';
import { useAuth } from '../context/AuthContext';

const EMPTY_FORM = {
    OrgName: '',
    OrgCode: '',
    Industry: 'Healthcare & MedTech',
    ContactEmail: '',
    ContactPhone: '',
    Address: '',
    createAdmin: true,
    adminName: '',
    adminEmail: '',
    adminPassword: '',
    adminDesignation: 'Organization Administrator'
};

const getInitials = (name) => {
    if (!name || typeof name !== 'string') return 'OG';
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return 'OG';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
};

const Organizations = () => {
    const navigate = useNavigate();
    const { isSuper, setActiveOrg } = useAuth();

    const [organizations, setOrganizations] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filters & Pagination
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [industryFilter, setIndustryFilter] = useState('ALL');
    const [viewMode, setViewMode] = useState('table'); // 'table' or 'cards'
    const [sortField, setSortField] = useState('OrgID');
    const [sortOrder, setSortOrder] = useState('asc');
    const [entries, setEntries] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    // Modal state
    const [modalOpen, setModalOpen] = useState(false);
    const [editingOrg, setEditingOrg] = useState(null);
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [showPassword, setShowPassword] = useState(false);
    const [saving, setSaving] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');

    const fetchOrganizations = async () => {
        try {
            setLoading(true);
            const res = await api.get('/organizations');
            setOrganizations(res.data || []);
        } catch (err) {
            console.error('Error fetching organizations:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrganizations();
    }, []);

    const openCreateModal = () => {
        setEditingOrg(null);
        setFormData(EMPTY_FORM);
        setShowPassword(false);
        setErrorMsg('');
        setModalOpen(true);
    };

    const openEditModal = (org) => {
        setEditingOrg(org);
        setFormData({
            OrgName: org.OrgName || '',
            OrgCode: org.OrgCode || '',
            Industry: org.Industry || 'Healthcare & MedTech',
            ContactEmail: org.ContactEmail || '',
            ContactPhone: org.ContactPhone || '',
            Address: org.Address || '',
            Status: org.Status || 'Active',
            createAdmin: false,
            adminName: '',
            adminEmail: '',
            adminPassword: '',
            adminDesignation: ''
        });
        setShowPassword(false);
        setErrorMsg('');
        setModalOpen(true);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setErrorMsg('');

        try {
            if (editingOrg) {
                // Update
                await api.put(`/organizations/${editingOrg.OrgID}`, {
                    OrgName: formData.OrgName,
                    OrgCode: formData.OrgCode,
                    Industry: formData.Industry,
                    ContactEmail: formData.ContactEmail,
                    ContactPhone: formData.ContactPhone,
                    Address: formData.Address,
                    Status: formData.Status
                });
            } else {
                // Create
                const payload = {
                    OrgName: formData.OrgName,
                    OrgCode: formData.OrgCode,
                    Industry: formData.Industry,
                    ContactEmail: formData.ContactEmail,
                    ContactPhone: formData.ContactPhone,
                    Address: formData.Address
                };
                if (formData.createAdmin && formData.adminEmail && formData.adminPassword) {
                    payload.adminName = formData.adminName;
                    payload.adminEmail = formData.adminEmail;
                    payload.adminPassword = formData.adminPassword;
                    payload.adminDesignation = formData.adminDesignation;
                }
                await api.post('/organizations', payload);
            }

            setModalOpen(false);
            await fetchOrganizations();
            window.dispatchEvent(new Event('sv_organizations_updated'));
        } catch (err) {
            setErrorMsg(err.response?.data?.error || 'Failed to save organization');
        } finally {
            setSaving(false);
        }
    };

    const handleToggleStatus = async (org) => {
        const nextStatus = org.Status === 'Active' ? 'Suspended' : 'Active';
        if (!window.confirm(`Are you sure you want to change status of "${org.OrgName}" to ${nextStatus}?`)) return;

        try {
            await api.patch(`/organizations/${org.OrgID}/status`, { Status: nextStatus });
            await fetchOrganizations();
            window.dispatchEvent(new Event('sv_organizations_updated'));
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to update status');
        }
    };

    const handleDelete = async (org) => {
        if (!window.confirm(`Are you sure you want to delete organization "${org.OrgName}"? This action cannot be undone.`)) return;

        try {
            await api.delete(`/organizations/${org.OrgID}`);
            await fetchOrganizations();
            window.dispatchEvent(new Event('sv_organizations_updated'));
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to delete organization');
        }
    };

    const handleSwitchToOrg = (org, targetPath = '/dashboard') => {
        setActiveOrg(org.OrgID, org.OrgName);
        navigate(targetPath);
    };

    // Metrics
    const totalOrgs = organizations.length;
    const activeOrgs = organizations.filter(o => o.Status === 'Active').length;
    const suspendedOrgs = totalOrgs - activeOrgs;
    const totalUsers = organizations.reduce((acc, o) => acc + Number(o.TotalUsers || 0), 0);
    const totalLeads = organizations.reduce((acc, o) => acc + Number(o.TotalLeads || 0), 0);
    const totalPipeline = organizations.reduce((acc, o) => acc + Number(o.TotalPipelineValue || 0), 0);

    // Unique industries for filter dropdown
    const uniqueIndustries = useMemo(() => {
        const set = new Set();
        organizations.forEach(o => {
            if (o.Industry) set.add(o.Industry);
        });
        return Array.from(set).sort();
    }, [organizations]);

    // Filtered list
    const filteredOrgs = useMemo(() => {
        return organizations.filter(o => {
            const term = search.toLowerCase().trim();
            const matchesSearch = !term ||
                o.OrgName?.toLowerCase().includes(term) ||
                (o.OrgCode && o.OrgCode.toLowerCase().includes(term)) ||
                (o.Industry && o.Industry.toLowerCase().includes(term)) ||
                (o.ContactEmail && o.ContactEmail.toLowerCase().includes(term)) ||
                (o.ContactPhone && o.ContactPhone.toLowerCase().includes(term)) ||
                String(o.OrgID).includes(term);

            const matchesStatus = statusFilter === 'ALL' || o.Status === statusFilter;
            const matchesIndustry = industryFilter === 'ALL' || o.Industry === industryFilter;
            return matchesSearch && matchesStatus && matchesIndustry;
        });
    }, [organizations, search, statusFilter, industryFilter]);

    // Sorted list
    const sortedOrgs = useMemo(() => {
        const list = [...filteredOrgs];
        list.sort((a, b) => {
            let valA = a[sortField];
            let valB = b[sortField];

            if (sortField === 'TotalUsers') {
                valA = Number(a.TotalUsers) || 0;
                valB = Number(b.TotalUsers) || 0;
            } else if (sortField === 'TotalLeads') {
                valA = Number(a.TotalLeads) || 0;
                valB = Number(b.TotalLeads) || 0;
            } else if (sortField === 'TotalPipelineValue') {
                valA = Number(a.TotalPipelineValue) || 0;
                valB = Number(b.TotalPipelineValue) || 0;
            } else if (sortField === 'OrgName') {
                valA = (a.OrgName || '').toLowerCase();
                valB = (b.OrgName || '').toLowerCase();
            }

            if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
            if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
            return 0;
        });
        return list;
    }, [filteredOrgs, sortField, sortOrder]);

    // Pagination
    const totalPages = Math.ceil(sortedOrgs.length / entries);
    const paginatedOrgs = useMemo(() => {
        const start = (currentPage - 1) * entries;
        return sortedOrgs.slice(start, start + entries);
    }, [sortedOrgs, currentPage, entries]);

    const handleSort = (field) => {
        if (sortField === field) {
            setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortOrder('asc');
        }
    };

    const isFiltered = Boolean(
        search.trim() ||
        statusFilter !== 'ALL' ||
        industryFilter !== 'ALL'
    );

    const handleResetFilters = () => {
        setSearch('');
        setStatusFilter('ALL');
        setIndustryFilter('ALL');
        setCurrentPage(1);
    };

    if (!isSuper) {
        return (
            <Layout>
                <div className="dome-dashboard-wrap">
                    <div className="dome-table-card p-40 text-center">
                        <div className="text-32 text-danger mb-12">
                            <FontAwesomeIcon icon={faShieldAlt} />
                        </div>
                        <h2 className="font-18 text-dark font-600 mb-6">Superadmin Access Required</h2>
                        <p className="text-muted font-13 mb-16">
                            Only the platform Superadmin has permission to administer tenant organizations and cross-org workspaces.
                        </p>
                        <button className="btn-dome-new" onClick={() => navigate('/dashboard')}>
                            Return to Dashboard
                        </button>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="dome-dashboard-wrap">

                {/* ================= 1. TOP HEADER ================= */}
                <div className="dome-dash-header-row">
                    <div className="dome-dash-title-wrap">
                        <h1 className="dome-dash-h1">Tenant Organizations</h1>
                    </div>

                    <div className="dome-dash-actions">
                        <button
                            type="button"
                            className="btn-dome-secondary"
                            onClick={fetchOrganizations}
                            title="Refresh organizations directory"
                            disabled={loading}
                        >
                            <FontAwesomeIcon icon={faSyncAlt} spin={loading} />
                            <span>Refresh</span>
                        </button>

                        <button
                            type="button"
                            className="btn-dome-new"
                            onClick={openCreateModal}
                        >
                            <FontAwesomeIcon icon={faPlus} />
                            <span>New Organization</span>
                        </button>
                    </div>
                </div>

                {/* ================= 2. 4-CARD KPI RIBBON ================= */}
                <div className="dome-kpi-strip">
                    {/* Card 1: Total Organizations */}
                    <div
                        className={`dome-kpi-card cursor-pointer ${statusFilter !== 'ALL' ? 'active-filter-card' : ''}`}
                        onClick={() => {
                            setStatusFilter(prev => prev === 'Active' ? 'ALL' : 'Active');
                            setCurrentPage(1);
                        }}
                        title="Filter Active Organizations"
                    >
                        <span className="dome-kpi-label">Organizations</span>
                        <div className="dome-kpi-val-row">
                            <span className="dome-kpi-num">{totalOrgs}</span>
                        </div>
                        <span className="dome-kpi-sub">
                            {activeOrgs} Active • {suspendedOrgs} Suspended
                        </span>
                    </div>

                    {/* Card 2: Assigned Users */}
                    <div className="dome-kpi-card">
                        <span className="dome-kpi-label">Assigned Users</span>
                        <div className="dome-kpi-val-row">
                            <span className="dome-kpi-num text-primary">{totalUsers}</span>
                        </div>
                        <span className="dome-kpi-sub">Across all tenant workspaces</span>
                    </div>

                    {/* Card 3: CRM Leads Managed */}
                    <div className="dome-kpi-card">
                        <span className="dome-kpi-label">CRM Leads Managed</span>
                        <div className="dome-kpi-val-row">
                            <span className="dome-kpi-num text-success">{totalLeads.toLocaleString()}</span>
                        </div>
                        <span className="dome-kpi-sub">Aggregated client database</span>
                    </div>

                    {/* Card 4: Total Pipeline Value */}
                    <div className="dome-kpi-card">
                        <span className="dome-kpi-label">Total Pipeline</span>
                        <div className="dome-kpi-val-row">
                            <span className="dome-kpi-num">${totalPipeline.toLocaleString()}</span>
                        </div>
                        <span className="dome-kpi-sub">Active deals and opportunities</span>
                    </div>
                </div>

                {/* ================= 3. FILTER TOOLBAR (2-ROW SYSTEM) ================= */}
                <div className="dome-dash-subnav-strip dome-opp-subnav">
                    {/* Row 1: Full-Width Search Box */}
                    <div className="dome-opp-search-row">
                        <div className="dome-search-full-box">
                            <FontAwesomeIcon icon={faSearch} className="dome-search-full-icon" />
                            <input
                                type="text"
                                placeholder="Search organizations by name, code, industry, contact email, phone..."
                                value={search}
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                    setCurrentPage(1);
                                }}
                                className="dome-search-full-input"
                            />
                            {search && (
                                <button
                                    type="button"
                                    className="dome-search-clear-mini"
                                    onClick={() => { setSearch(''); setCurrentPage(1); }}
                                    title="Clear search"
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Row 2: Stacked Filters */}
                    <div className="dome-opp-filters-row">
                        {/* Status Filter */}
                        <div className="dome-stacked-filter-item">
                            <label className="dome-stacked-filter-label">Status</label>
                            <select
                                value={statusFilter}
                                onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                                className="dome-stacked-select"
                            >
                                <option value="ALL">All Statuses ({organizations.length})</option>
                                <option value="Active">Active ({activeOrgs})</option>
                                <option value="Suspended">Suspended ({suspendedOrgs})</option>
                            </select>
                        </div>

                        {/* Industry Filter */}
                        {uniqueIndustries.length > 0 && (
                            <div className="dome-stacked-filter-item">
                                <label className="dome-stacked-filter-label">Industry</label>
                                <select
                                    value={industryFilter}
                                    onChange={(e) => { setIndustryFilter(e.target.value); setCurrentPage(1); }}
                                    className="dome-stacked-select"
                                >
                                    <option value="ALL">All Industries</option>
                                    {uniqueIndustries.map(ind => (
                                        <option key={ind} value={ind}>{ind}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* View Switcher: Table vs Cards */}
                        <div className="dome-stacked-filter-item">
                            <label className="dome-stacked-filter-label">View</label>
                            <div className="dome-view-toggle-group">
                                <button
                                    type="button"
                                    className={`dome-view-toggle-btn ${viewMode === 'table' ? 'active' : ''}`}
                                    onClick={() => setViewMode('table')}
                                    title="Table View"
                                >
                                    <FontAwesomeIcon icon={faTable} />
                                    <span>Table</span>
                                </button>
                                <button
                                    type="button"
                                    className={`dome-view-toggle-btn ${viewMode === 'cards' ? 'active' : ''}`}
                                    onClick={() => setViewMode('cards')}
                                    title="Cards View"
                                >
                                    <FontAwesomeIcon icon={faThLarge} />
                                    <span>Cards</span>
                                </button>
                            </div>
                        </div>

                        {/* Reset Filters Button */}
                        {isFiltered && (
                            <div className="dome-stacked-filter-item dome-stacked-reset-item">
                                <label className="dome-stacked-filter-label">&nbsp;</label>
                                <button
                                    type="button"
                                    className="dome-clear-filters-mini"
                                    onClick={handleResetFilters}
                                    title="Reset all filters"
                                >
                                    <FontAwesomeIcon icon={faTimes} className="mr-4px" />
                                    Clear
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* ================= 4. MAIN CONTENT CARD ================= */}
                <div className="dome-dash-card">
                    <div className="dome-dash-card-header">
                        <h3 className="dome-dash-card-title">Tenant Organizations Directory</h3>
                        <div className="dome-table-header-controls">
                            <div className="dome-mini-filter-item">
                                <span className="dome-filter-mini-label">Sort:</span>
                                <select
                                    value={sortField}
                                    onChange={(e) => setSortField(e.target.value)}
                                    className="dome-mini-select"
                                >
                                    <option value="OrgID">ID</option>
                                    <option value="OrgName">Organization Name</option>
                                    <option value="TotalUsers">Users</option>
                                    <option value="TotalLeads">Leads</option>
                                    <option value="TotalPipelineValue">Pipeline</option>
                                </select>
                                <button
                                    type="button"
                                    className="dome-sort-dir-mini"
                                    onClick={() => setSortOrder(o => o === 'asc' ? 'desc' : 'asc')}
                                    title={`Sort Order: ${sortOrder === 'asc' ? 'Ascending' : 'Descending'}`}
                                >
                                    <FontAwesomeIcon icon={sortOrder === 'asc' ? faSortAmountDown : faSortAmountUp} />
                                </button>
                            </div>

                            <div className="dome-mini-filter-item">
                                <span className="dome-filter-mini-label">Show:</span>
                                <select
                                    value={entries}
                                    onChange={(e) => {
                                        setEntries(Number(e.target.value));
                                        setCurrentPage(1);
                                    }}
                                    className="dome-mini-select"
                                >
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {loading ? (
                        <div className="p-20 text-center text-muted font-13">
                            <FontAwesomeIcon icon={faSyncAlt} spin className="mr-6px text-primary" />
                            Loading tenant organizations directory...
                        </div>
                    ) : filteredOrgs.length === 0 ? (
                        <div className="p-40 text-center text-muted font-13">
                            <div className="mb-10 text-24 text-muted">🏢</div>
                            <p className="font-14 text-dark font-600 mb-4">No organizations found</p>
                            <p className="text-muted text-12 mb-16">
                                {isFiltered ? "No organizations match your current search/filter criteria." : "Get started by creating your first tenant organization."}
                            </p>
                            {isFiltered ? (
                                <button
                                    type="button"
                                    className="btn-dome-secondary"
                                    onClick={handleResetFilters}
                                >
                                    Reset Filters
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className="btn-dome-new"
                                    onClick={openCreateModal}
                                >
                                    <FontAwesomeIcon icon={faPlus} />
                                    <span>Create Organization</span>
                                </button>
                            )}
                        </div>
                    ) : viewMode === 'table' ? (
                        /* ================= TABLE VIEW ================= */
                        <div className="dome-dash-table-wrap">
                            <table className="dome-dash-table dome-orgs-table">
                                <thead>
                                    <tr>
                                        <th className="th-id cursor-pointer" onClick={() => handleSort('OrgID')}>
                                            ID {sortField === 'OrgID' && (
                                                <FontAwesomeIcon icon={sortOrder === 'asc' ? faSortAmountUp : faSortAmountDown} className="sort-icon-inline" />
                                            )}
                                        </th>
                                        <th onClick={() => handleSort('OrgName')} className="cursor-pointer">
                                            Organization {sortField === 'OrgName' && (
                                                <FontAwesomeIcon icon={sortOrder === 'asc' ? faSortAmountUp : faSortAmountDown} className="sort-icon-inline" />
                                            )}
                                        </th>
                                        <th>Code</th>
                                        <th>Industry</th>
                                        <th>Status</th>
                                        <th onClick={() => handleSort('TotalUsers')} className="cursor-pointer text-center">
                                            Users {sortField === 'TotalUsers' && (
                                                <FontAwesomeIcon icon={sortOrder === 'asc' ? faSortAmountUp : faSortAmountDown} className="sort-icon-inline" />
                                            )}
                                        </th>

                                        <th>Primary Phone</th>
                                        <th>Primary Email</th>
                                        <th className="text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedOrgs.map(org => {
                                        const isDefault = Number(org.OrgID) === 1;
                                        const initials = getInitials(org.OrgName);

                                        return (
                                            <tr key={org.OrgID}>
                                                {/* ID */}
                                                <td>
                                                    <span className="dome-id-pill">{org.OrgID}</span>
                                                </td>

                                                {/* Organization Name */}
                                                <td>
                                                    <div className="d-flex align-center gap-10">
                                                        <div className="dome-lead-name-col">
                                                            <span
                                                                className="dome-lead-name-link cursor-pointer"
                                                                onClick={() => handleSwitchToOrg(org, '/dashboard')}
                                                                title="Switch workspace & open Dashboard"
                                                            >
                                                                {org.OrgName}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </td>

                                                {/* Code */}
                                                <td>{org.OrgCode || '—'}</td>

                                                {/* Industry */}
                                                <td>{org.Industry || 'Healthcare & MedTech'}</td>

                                                {/* Status */}
                                                <td>
                                                    <span className={`badge-status ${org.Status === 'Active' ? 'badge-paid' : 'badge-rejected'}`}>
                                                        <span className={`dome-dot ${org.Status === 'Active' ? 'dot-paid' : 'dot-rejected'}`} />
                                                        {org.Status}
                                                    </span>
                                                </td>

                                                {/* Users */}
                                                <td className="text-center">{Number(org.TotalUsers || 0)}</td>

                                                {/* Primary Phone */}
                                                <td>
                                                    <div>
                                                        <div>{org.ContactPhone || '—'}</div>
                                                    </div>
                                                </td>

                                                {/* Primary Email */}
                                                <td>
                                                    <div>
                                                        <div>{org.ContactEmail || '—'}</div>
                                                    </div>
                                                </td>

                                                {/* Actions */}
                                                <td>
                                                    <div className="dome-row-actions" onClick={(e) => e.stopPropagation()}>
                                                        <button
                                                            type="button"
                                                            className="dome-icon-btn"
                                                            onClick={() => handleSwitchToOrg(org, '/dashboard')}
                                                            title="Switch workspace & open Dashboard"
                                                        >
                                                            <FontAwesomeIcon icon={faExternalLinkAlt} />
                                                        </button>

                                                        <button
                                                            type="button"
                                                            className="dome-icon-btn"
                                                            onClick={() => handleSwitchToOrg(org, '/users')}
                                                            title="Manage users for this organization"
                                                        >
                                                            <FontAwesomeIcon icon={faUsers} />
                                                        </button>

                                                        <button
                                                            type="button"
                                                            className="dome-icon-btn"
                                                            onClick={() => openEditModal(org)}
                                                            title="Edit organization profile"
                                                        >
                                                            <FontAwesomeIcon icon={faEdit} />
                                                        </button>

                                                        {!isDefault && (
                                                            <>
                                                                <button
                                                                    type="button"
                                                                    className={`dome-icon-btn ${org.Status === 'Active' ? '' : 'text-success'}`}
                                                                    onClick={() => handleToggleStatus(org)}
                                                                    title={org.Status === 'Active' ? 'Suspend Organization' : 'Activate Organization'}
                                                                >
                                                                    <FontAwesomeIcon icon={org.Status === 'Active' ? faBan : faCheckCircle} />
                                                                </button>

                                                                <button
                                                                    type="button"
                                                                    className="dome-icon-btn dome-icon-btn-danger"
                                                                    onClick={() => handleDelete(org)}
                                                                    title="Delete organization"
                                                                >
                                                                    <FontAwesomeIcon icon={faTrash} />
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        /* ================= CARDS VIEW ================= */
                        <div className="dome-targets-grid">
                            {paginatedOrgs.map(org => {
                                const isDefault = Number(org.OrgID) === 1;
                                const initials = getInitials(org.OrgName);

                                return (
                                    <div key={org.OrgID} className="dome-target-card">
                                        <div className="dome-target-card-header">
                                            <div className="dome-target-user-info">
                                                <div className="dome-target-avatar">
                                                    {initials}
                                                </div>
                                                <div>
                                                    <h4
                                                        className="dome-target-user-name cursor-pointer"
                                                        onClick={() => handleSwitchToOrg(org, '/dashboard')}
                                                    >
                                                        {org.OrgName}
                                                    </h4>
                                                    <p className="dome-target-user-role">{org.Industry || 'Healthcare & MedTech'}</p>
                                                </div>
                                            </div>

                                            <span className={`badge-status ${org.Status === 'Active' ? 'badge-paid' : 'badge-rejected'}`}>
                                                <span className={`dome-dot ${org.Status === 'Active' ? 'dot-paid' : 'dot-rejected'}`} />
                                                {org.Status}
                                            </span>
                                        </div>

                                        <div className="dome-target-metrics-box">
                                            <div className="dome-target-metrics-row">
                                                <div className="dome-target-stat-col">
                                                    <span className="dome-target-stat-lbl">Assigned Users</span>
                                                    <span className="dome-target-stat-val val-blue">
                                                        {Number(org.TotalUsers || 0)} Users
                                                    </span>
                                                </div>
                                                <div className="dome-target-stat-col text-right">
                                                    <span className="dome-target-stat-lbl">Pipeline Value</span>
                                                    <span className="dome-target-stat-val">
                                                        ${Number(org.TotalPipelineValue || 0).toLocaleString()}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="dome-target-metrics-row text-muted font-11 mt-4">
                                                <span>Code: <strong>{org.OrgCode || '—'}</strong></span>
                                                <span>Leads: <strong>{Number(org.TotalLeads || 0).toLocaleString()}</strong></span>
                                            </div>
                                        </div>

                                        <div className="dome-target-meta-details">
                                            {org.ContactEmail && (
                                                <div className="dome-target-meta-item">
                                                    <FontAwesomeIcon icon={faEnvelope} className="text-muted" />
                                                    <span>{org.ContactEmail}</span>
                                                </div>
                                            )}
                                            {org.ContactPhone && (
                                                <div className="dome-target-meta-item">
                                                    <FontAwesomeIcon icon={faPhone} className="text-muted" />
                                                    <span>{org.ContactPhone}</span>
                                                </div>
                                            )}
                                            {org.Address && (
                                                <div className="dome-target-meta-item">
                                                    <FontAwesomeIcon icon={faMapMarkerAlt} className="text-muted" />
                                                    <span>{org.Address}</span>
                                                </div>
                                            )}
                                        </div>

                                        <div className="dome-target-card-footer">
                                            <span className="font-11 text-muted">ID: #{org.OrgID}</span>
                                            <div className="dome-row-actions">
                                                <button
                                                    type="button"
                                                    className="dome-icon-btn"
                                                    onClick={() => handleSwitchToOrg(org, '/dashboard')}
                                                    title="Switch workspace & open Dashboard"
                                                >
                                                    <FontAwesomeIcon icon={faExternalLinkAlt} />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="dome-icon-btn"
                                                    onClick={() => handleSwitchToOrg(org, '/users')}
                                                    title="Manage users"
                                                >
                                                    <FontAwesomeIcon icon={faUsers} />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="dome-icon-btn"
                                                    onClick={() => openEditModal(org)}
                                                    title="Edit organization profile"
                                                >
                                                    <FontAwesomeIcon icon={faEdit} />
                                                </button>
                                                {!isDefault && (
                                                    <button
                                                        type="button"
                                                        className="dome-icon-btn dome-icon-btn-danger"
                                                        onClick={() => handleDelete(org)}
                                                        title="Delete organization"
                                                    >
                                                        <FontAwesomeIcon icon={faTrash} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* ================= 5. TABLE FOOTER & PAGINATION ================= */}
                    <div className="dome-table-footer">
                        <span className="dome-table-counter">
                            Showing {sortedOrgs.length === 0 ? 0 : (currentPage - 1) * entries + 1}&ndash;{Math.min(currentPage * entries, sortedOrgs.length)} of {sortedOrgs.length} entries
                            {isFiltered && ` (filtered from ${organizations.length} total)`}
                        </span>

                        <div className="dome-pagination-controls">
                            <button
                                type="button"
                                className="dome-page-btn"
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(p => p - 1)}
                            >
                                Prev
                            </button>

                            <span className="dome-page-indicator">
                                Page {currentPage} of {totalPages || 1}
                            </span>

                            <button
                                type="button"
                                className="dome-page-btn"
                                disabled={currentPage === totalPages || totalPages === 0}
                                onClick={() => setCurrentPage(p => p + 1)}
                            >
                                Next
                            </button>
                        </div>
                    </div>

                </div>

                {/* ================= CREATE / EDIT MODAL ================= */}
                {modalOpen && (
                    <div className="modal-overlay" onClick={() => setModalOpen(false)}>
                        <div className="modal dome-modal-card" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header modal-header-flex">
                                <h3>{editingOrg ? `Edit Organization (${editingOrg.OrgID})` : "Create New Organization"}</h3>
                                <button
                                    type="button"
                                    onClick={() => setModalOpen(false)}
                                    className="modal-btn-close"
                                    title="Close Modal"
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            </div>

                            <form onSubmit={handleSave} className="d-flex flex-col flex-1 overflow-hidden">
                                <div className="modal-body">
                                    {errorMsg && (
                                        <div className="modal-error-alert mb-12">{errorMsg}</div>
                                    )}

                                    <div className="form-grid">
                                        {/* Organization Name */}
                                        <div className="input-group input-group-span-2">
                                            <label>Organization Name <span className="text-danger">*</span></label>
                                            <input
                                                type="text"
                                                required
                                                placeholder="e.g. Acme Health Network"
                                                value={formData.OrgName}
                                                onChange={(e) => setFormData({ ...formData, OrgName: e.target.value })}
                                            />
                                        </div>

                                        {/* Organization Code */}
                                        <div className="input-group">
                                            <label>Organization Code <span className="text-danger">*</span></label>
                                            <input
                                                type="text"
                                                required
                                                placeholder="e.g. ACME"
                                                value={formData.OrgCode}
                                                onChange={(e) => setFormData({ ...formData, OrgCode: e.target.value.toUpperCase() })}
                                            />
                                        </div>

                                        {/* Industry */}
                                        <div className="input-group">
                                            <label>Industry</label>
                                            <select
                                                value={formData.Industry}
                                                onChange={(e) => setFormData({ ...formData, Industry: e.target.value })}
                                            >
                                                <option value="Healthcare & MedTech">Healthcare & MedTech</option>
                                                <option value="Biotech & Pharma">Biotech & Pharma</option>
                                                <option value="Enterprise SaaS">Enterprise SaaS</option>
                                                <option value="Financial Services">Financial Services</option>
                                                <option value="Manufacturing">Manufacturing</option>
                                                <option value="Other">Other</option>
                                            </select>
                                        </div>

                                        {/* Contact Email */}
                                        <div className="input-group">
                                            <label>Contact Email</label>
                                            <input
                                                type="email"
                                                placeholder="contact@organization.com"
                                                value={formData.ContactEmail}
                                                onChange={(e) => setFormData({ ...formData, ContactEmail: e.target.value })}
                                            />
                                        </div>

                                        {/* Contact Phone */}
                                        <div className="input-group">
                                            <label>Contact Phone</label>
                                            <input
                                                type="text"
                                                placeholder="+1 (555) 000-0000"
                                                value={formData.ContactPhone}
                                                onChange={(e) => setFormData({ ...formData, ContactPhone: e.target.value })}
                                            />
                                        </div>

                                        {/* Address */}
                                        <div className="input-group input-group-span-2">
                                            <label>Headquarters Address</label>
                                            <input
                                                type="text"
                                                placeholder="123 Innovation Way, Suite 400, Boston, MA"
                                                value={formData.Address}
                                                onChange={(e) => setFormData({ ...formData, Address: e.target.value })}
                                            />
                                        </div>

                                        {/* Status (Edit only) */}
                                        {editingOrg && (
                                            <div className="input-group">
                                                <label>Operational Status</label>
                                                <select
                                                    value={formData.Status}
                                                    onChange={(e) => setFormData({ ...formData, Status: e.target.value })}
                                                >
                                                    <option value="Active">Active</option>
                                                    <option value="Suspended">Suspended</option>
                                                </select>
                                            </div>
                                        )}

                                        {/* OPTIONAL: Create Tenant Admin (Only on new org) */}
                                        {!editingOrg && (
                                            <>
                                                <div className="input-group-span-2 create-admin-row">
                                                    <input
                                                        type="checkbox"
                                                        id="createAdminCheck"
                                                        checked={formData.createAdmin}
                                                        onChange={(e) =>
                                                            setFormData({
                                                                ...formData,
                                                                createAdmin: e.target.checked
                                                            })
                                                        }
                                                    />

                                                    <label htmlFor="createAdminCheck">
                                                        Provision Tenant Administrator Account
                                                    </label>
                                                </div>

                                                {formData.createAdmin && (
                                                    <>
                                                        <div className="input-group">
                                                            <label>Admin Full Name <span className="text-danger">*</span></label>
                                                            <input
                                                                type="text"
                                                                required={formData.createAdmin}
                                                                placeholder="e.g. John Doe"
                                                                value={formData.adminName}
                                                                onChange={(e) => setFormData({ ...formData, adminName: e.target.value })}
                                                            />
                                                        </div>

                                                        <div className="input-group">
                                                            <label>Admin Email <span className="text-danger">*</span></label>
                                                            <input
                                                                type="email"
                                                                required={formData.createAdmin}
                                                                placeholder="admin@organization.com"
                                                                value={formData.adminEmail}
                                                                onChange={(e) => setFormData({ ...formData, adminEmail: e.target.value })}
                                                            />
                                                        </div>

                                                        <div className="input-group">
                                                            <label>Admin Password <span className="text-danger">*</span></label>
                                                            <div className="pos-relative">
                                                                <input
                                                                    type={showPassword ? 'text' : 'password'}
                                                                    required={formData.createAdmin}
                                                                    placeholder="Temporary password"
                                                                    value={formData.adminPassword}
                                                                    onChange={(e) => setFormData({ ...formData, adminPassword: e.target.value })}
                                                                />
                                                            </div>
                                                        </div>

                                                        <div className="input-group">
                                                            <label>Admin Designation</label>
                                                            <input
                                                                type="text"
                                                                placeholder="Organization Administrator"
                                                                value={formData.adminDesignation}
                                                                onChange={(e) => setFormData({ ...formData, adminDesignation: e.target.value })}
                                                            />
                                                        </div>
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>

                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn-dome-secondary"
                                        onClick={() => setModalOpen(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn-dome-new"
                                        disabled={saving}
                                    >
                                        {saving ? 'Saving...' : editingOrg ? "Save Changes" : "Create Organization"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

            </div>
        </Layout>
    );
};

export default Organizations;
